import { diToFormData, type VaultFormData } from '@onefootprint/core';
import type { CustomRequirementCondition, DataIdentifier } from '@onefootprint/request-types';
import type { UseFormGetValues } from 'react-hook-form';

export const getDis = (condition: CustomRequirementCondition): (keyof VaultFormData)[] => {
  if (condition.kind === 'expression') {
    // Transform the DI to form data format (e.g., custom.likes_pets -> custom__DOT__likes_pets)
    return [diToFormData(condition.field as DataIdentifier) as keyof VaultFormData];
  }
  if (condition.kind === 'and' || condition.kind === 'or') {
    return condition.conditions.flatMap(c => getDis(c));
  }
  return [];
};

export const evaluateCondition = (
  condition: CustomRequirementCondition,
  getFormValue: UseFormGetValues<VaultFormData>,
): boolean => {
  if (condition.kind === 'expression') {
    // Transform the DI to form data format (e.g., custom.likes_pets -> custom__DOT__likes_pets)
    const formFieldName = diToFormData(condition.field as DataIdentifier) as keyof VaultFormData;
    const value = getFormValue(formFieldName);
    if (value === undefined) {
      // If there is no value for this field yet, opt against rendering this section
      return false;
    }
    if (condition.op === 'eq') return value === condition.value;
    if (condition.op === 'not_eq') return value !== condition.value;
    if (condition.op === 'exists') return !!value;
    if (condition.op === 'in') return !Array.isArray(value) && condition.values.includes(value);
    if (condition.op === 'not_in') return !Array.isArray(value) && !condition.values.includes(value);
    // @ts-expect-error: `condition.op` should generally be of type `never`
    console.error(`Unsupported custom data condition op: ${condition.op}`);
  }
  if (condition.kind === 'and') {
    return condition.conditions.every(c => evaluateCondition(c, getFormValue));
  }
  if (condition.kind === 'or') {
    return condition.conditions.some(c => evaluateCondition(c, getFormValue));
  }
  return false;
};
